import { Check, Minus, Landmark, ShieldCheck, MapPin } from "lucide-react";
import { FEATURE_GROUPS, FINANCIAL_NOTICE, AVAILABILITY_NOTICE, planScope } from "./landingPlans";

const eur = (v) => `${Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;

function Cell({ v, testid }) {
  if (v === true) return <td className="px-3 py-3 text-center" data-testid={testid}><Check className="h-4 w-4 text-emerald-400 inline-block" aria-label="Inclus" /></td>;
  if (v === false) return <td className="px-3 py-3 text-center" data-testid={testid}><Minus className="h-4 w-4 text-slate-500 inline-block" aria-label="Sans objet" /></td>;
  return <td className="px-3 py-3 text-xs text-slate-300 leading-snug" data-testid={testid}>{v}</td>;
}

export function FeatureTable({ plans }) {
  return (
    <div className="mt-12" data-testid="landing-feature-table">
      <p className="text-xs uppercase tracking-[0.2em] text-brass">Détail des fonctionnalités</p>
      <h3 className="display mt-3 text-2xl sm:text-3xl text-white leading-tight">Toutes les formules incluent toutes les fonctionnalités. Seule la limite de logements change.</h3>
      <p className="mt-2 text-sm text-slate-300 max-w-3xl">Certaines fonctions s'appliquent différemment selon votre statut juridique, choisi à l'ouverture du compte : <span className="text-white">agence immobilière titulaire de la carte G</span> ou <span className="text-white">conciergerie</span>.</p>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="landing-plan-limits">
        {plans.map((p) => {
          const m = p.prices.find((x) => x.interval === "month") || p.prices[0];
          const y = p.prices.find((x) => x.interval === "year");
          return (
            <div key={p.key} className="rounded-xl border border-white/10 bg-[#071e42]/60 px-4 py-3" data-testid={`landing-plan-limit-${p.key}`}>
              <p className="text-sm text-white font-medium">{p.name.replace("Casanéo ", "")}</p>
              <p className="text-xs text-slate-400 mt-0.5">{planScope(p)}</p>
              <p className="text-xs text-slate-300 mt-1">{p.per_property ? `${eur(m.amount)} par logement et par mois HT` : `${eur(m.amount)}/mois HT`}{y ? ` · ${eur(y.amount)}${p.per_property ? " par logement" : ""}/an HT` : ""}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10 bg-[#071e42]/60">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-white/10 text-left">
              <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium w-1/2">Fonctionnalité (toutes formules)</th>
              <th className="px-3 py-3 text-xs uppercase tracking-wider text-brass font-medium">Agence carte G</th>
              <th className="px-3 py-3 text-xs uppercase tracking-wider text-brass font-medium">Conciergerie</th>
            </tr>
          </thead>
          <tbody>
            {FEATURE_GROUPS.map((g) => (
              <FragmentGroup key={g.title} group={g} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex gap-3" data-testid="landing-financial-notice">
          <Landmark className="h-5 w-5 text-brass shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-white font-medium">Casanéo n'est pas un service financier</p>
            <p className="mt-1.5 text-xs text-slate-300 leading-relaxed">{FINANCIAL_NOTICE}</p>
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex gap-3" data-testid="landing-availability-notice">
          <MapPin className="h-5 w-5 text-brass shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-white font-medium">Disponibilité commerciale</p>
            <p className="mt-1.5 text-xs text-slate-300 leading-relaxed">{AVAILABILITY_NOTICE}</p>
            <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-slate-400"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> Éditeur : SAS MHP Immobilier, Nice — agence titulaire de la carte G.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FragmentGroup({ group }) {
  return (
    <>
      <tr className="bg-white/[0.03]"><th colSpan={3} className="px-4 py-2 text-left text-[11px] uppercase tracking-[0.18em] text-brass font-medium">{group.title}</th></tr>
      {group.rows.map((r, i) => (
        <tr key={r.label} className="border-t border-white/5 align-top">
          <td className="px-4 py-3 text-slate-200 text-xs sm:text-sm leading-snug">{r.label}</td>
          <Cell v={r.g} testid={`feature-${group.title.slice(0, 3)}-${i}-g`} />
          <Cell v={r.c} testid={`feature-${group.title.slice(0, 3)}-${i}-c`} />
        </tr>
      ))}
    </>
  );
}
